/**
 * confetti.js
 * Lightweight, dependency-free confetti burst, triggered when a product
 * is successfully added to the cart.
 *
 * INTEGRATION:
 * 1. Save this file as assets/confetti.js in your theme.
 * 2. Load it once, e.g. in cart-drawer.liquid or theme.liquid:
 *      <script src="{{ 'confetti.js' | asset_url }}" type="module"></script>
 * 3. It fires confetti in exactly two situations:
 *      a) A successful add-to-cart request (/cart/add.js or
 *         /cart/add.json) — waits briefly for the drawer to open, then
 *         fires inside it. Opening the drawer manually (e.g. clicking
 *         the cart icon on an existing/empty cart) never fires this.
 *      b) A line item's quantity is INCREASED while the drawer is
 *         already open (e.g. the +/- stepper), detected by diffing the
 *         cart returned from /cart/change.js or /cart/update.js against
 *         the last known quantities.
 *    Decreasing a quantity, removing an item, or just opening an
 *    existing cart never triggers confetti.
 */

const COLORS = ['#ff595e', '#ffca3a', '#8ac926', '#1982c4', '#6a4c93'];

function createConfettiPiece(container, containerWidth, containerHeight) {
  const piece = document.createElement('div');
  const size = 6 + Math.random() * 6;
  const color = COLORS[Math.floor(Math.random() * COLORS.length)];

  const startX = Math.random() * containerWidth;

  piece.style.position = 'absolute';
  piece.style.left = `${startX}px`;
  piece.style.top = '-16px';
  piece.style.width = `${size}px`;
  piece.style.height = `${size * 0.4}px`;
  piece.style.backgroundColor = color;
  piece.style.opacity = '1';
  piece.style.pointerEvents = 'none';
  piece.style.borderRadius = '1px';
  piece.style.willChange = 'transform, opacity';

  container.appendChild(piece);

  // Gentle side-to-side drift on the way down, like falling confetti/leaves
  const drift = (Math.random() - 0.5) * 120;
  const fallDistance = containerHeight + 40;
  const spins = (Math.random() > 0.5 ? 1 : -1) * (360 + Math.random() * 360);
  const duration = 1400 + Math.random() * 900;

  const animation = piece.animate(
    [
      { transform: `translate(0px, 0px) rotate(0deg)`, opacity: 1 },
      {
        transform: `translate(${drift * 0.5}px, ${fallDistance * 0.6}px) rotate(${spins * 0.6}deg)`,
        opacity: 1,
        offset: 0.7,
      },
      {
        transform: `translate(${drift}px, ${fallDistance}px) rotate(${spins}deg)`,
        opacity: 0,
      },
    ],
    {
      duration,
      easing: 'ease-in',
      fill: 'forwards',
    }
  );

  animation.onfinish = () => piece.remove();
}

/**
 * Launches a top-to-bottom confetti "shower" inside the given container.
 * Pieces spawn along the top edge at staggered times and fall downward.
 * @param {HTMLElement} container - element to render confetti inside
 * @param {number} count - number of confetti pieces
 */
export function launchConfetti(container, count = 70) {
  const target = container || document.body;

  const wrapper = document.createElement('div');
  wrapper.style.position = 'absolute';
  wrapper.style.inset = '0';
  wrapper.style.overflow = 'hidden';
  wrapper.style.pointerEvents = 'none';
  wrapper.style.zIndex = '9999';

  const computedPosition = getComputedStyle(target).position;
  if (computedPosition === 'static') {
    target.style.position = 'relative';
  }

  target.appendChild(wrapper);

  const rect = target.getBoundingClientRect();

  for (let i = 0; i < count; i++) {
    // Spread spawn times across ~900ms so it reads as a continuous shower
    setTimeout(() => createConfettiPiece(wrapper, rect.width, rect.height), Math.random() * 900);
  }

  setTimeout(() => wrapper.remove(), 2600);
}

function getCartDrawerElement() {
  // Adjust this selector to match your theme's cart drawer element/tag.
  return (
    document.querySelector('cart-drawer-component') ||
    document.querySelector('cart-drawer') ||
    document.querySelector('#cart-drawer') ||
    document.querySelector('[data-cart-drawer]')
  );
}

/**
 * IMPORTANT: if your cart drawer is (or contains) a native <dialog>
 * element, the browser renders it in the "top layer" — a stacking
 * context that sits above everything else on the page, regardless of
 * z-index. Confetti appended anywhere outside that dialog will always
 * render BEHIND it. So we need to:
 *   1. Find the dialog that's actually open (has the `open` attribute)
 *   2. Append confetti as a descendant of that dialog, not elsewhere
 */
function getOpenDialogTarget(drawer) {
  if (!drawer) return null;

  if (drawer.tagName === 'DIALOG' && drawer.hasAttribute('open')) {
    return drawer;
  }

  const nestedDialog = drawer.querySelector('dialog[open]');
  if (nestedDialog) return nestedDialog;

  return document.querySelector('dialog[open]') || null;
}

/* -----------------------------------------------------------------------
 * TRIGGER 1: the drawer opening, for ANY reason (add-to-cart, clicking the
 * cart icon, etc.). We watch the dialog's `open` attribute directly rather
 * than hooking a specific action, so every way of opening it is covered.
 * --------------------------------------------------------------------- */
/* -----------------------------------------------------------------------
 * TRIGGER 1: a successful add-to-cart request (/cart/add.js or
 * /cart/add.json). We wait briefly for the drawer to actually open, then
 * fire confetti inside it. Opening the drawer manually (e.g. clicking the
 * cart icon) never matches this and never fires confetti.
 * --------------------------------------------------------------------- */
function waitForOpenDrawer(maxWaitMs = 1000) {
  return new Promise((resolve) => {
    const start = performance.now();

    function check() {
      const dialog = getOpenDialogTarget(getCartDrawerElement());
      if (dialog) {
        resolve(dialog);
      } else if (performance.now() - start < maxWaitMs) {
        requestAnimationFrame(check);
      } else {
        resolve(null);
      }
    }

    check();
  });
}

async function handleCartAdded() {
  const dialog = await waitForOpenDrawer();
  if (dialog) {
    launchConfetti(dialog);
  }
  primeCartQuantities(); // re-sync baseline so the new item has a known quantity
}

/* -----------------------------------------------------------------------
 * TRIGGER 2: a quantity INCREASE inside an already-open drawer (the +/-
 * stepper), which calls /cart/change.js or /cart/update.js. These
 * requests return the updated cart as JSON, so we diff each line item's
 * quantity against the last known value. A decrease or removal never
 * matches the ">" comparison, so it's silently ignored.
 * --------------------------------------------------------------------- */
let cartQuantities = {};

function primeCartQuantities() {
  fetch('/cart.js')
    .then((res) => res.json())
    .then((cart) => {
      (cart.items || []).forEach((item) => {
        cartQuantities[item.key] = item.quantity;
      });
    })
    .catch(() => {
      /* non-fatal: worst case, the first change won't be compared */
    });
}

function extractItems(payload) {
  // Most cart endpoints return { items: [...] } directly. Some custom
  // setups nest it as { cart: { items: [...] } }. Handle both.
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.cart?.items)) return payload.cart.items;
  return [];
}

function checkForQuantityIncrease(payload) {
  let increased = false;
  extractItems(payload).forEach((item) => {
    const previous = cartQuantities[item.key];
    if (typeof previous === 'number' && item.quantity > previous) {
      increased = true;
    }
    cartQuantities[item.key] = item.quantity;
  });
  return increased;
}

primeCartQuantities();

const originalFetch = window.fetch;
window.fetch = function (...args) {
  const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
  const isAddToCart = /\/cart\/add(\.js|\.json)?(\/|\?|$)/.test(url);
  const isCartChange = /\/cart\/(change|update)(\.js|\.json)?(\/|\?|$)/.test(url);

  const responsePromise = originalFetch.apply(this, args);

  if (isAddToCart) {
    responsePromise.then((response) => {
      if (response.ok) {
        setTimeout(handleCartAdded, 150);
      }
    });
    return responsePromise;
  }

  if (isCartChange) {
    return responsePromise.then((response) => {
      if (response.ok) {
        response
          .clone()
          .json()
          .then((cart) => {
            if (checkForQuantityIncrease(cart)) {
              const dialog = getOpenDialogTarget(getCartDrawerElement());
              if (dialog) launchConfetti(dialog);
            }
          })
          .catch(() => {
            /* response wasn't JSON in the shape we expect; ignore */
          });
      }
      return response;
    });
  }

  return responsePromise;
};